import * as React from "react";
import dayjs from "dayjs";

import { Box, Grid, Stack, Typography } from "@mui/material";

import CustomInput from "../../../../helper-components/input/CustomInput";
import CustomButton from "../../../../helper-components/input/CustomButton";
import CustomTooltip from "../../../../helper-components/misc/CustomTooltip";
import ControlledAccordion from "../../../../helper-components/form/ControlledAccordion";

import CustomSelection from "../../../../helper-components/form/CustomSelection";

import { validateRow } from "../../../../helper-functions/validation/objectValidation";
import { querySelectLibraries } from "../../../../helper-functions/api/libraryQueries";
import { querySelectResources } from "../../../../helper-functions/api/resourceQueries";
import { querySelectAuthors } from "../../../../helper-functions/api/authorQueries";
import { addFormToObj, getEllipsis, setInsertObjectState, setValue } from "../../../../helper-functions/shared/miscUtils";

import { getLanguage } from "../../../../config/language/language";
import { HIGHLIGHT_REQUEST_OBJECT } from "../../../../config/constants/Constants";
import { HIGHLIGHT_REQUEST_VALIDATION, IS_HIGHLIGHT_REQUEST_VALID } from "../../../../helper-functions/validation/highlightValidation";

/**
 * A form to update a highlight request.
 */
export default function HighlightRequestEditForm(props) {
    // Because modals don't re-render if the props' state changes, use a callback.
    const { callback, row: existingRow = {} } = props;

    const object = HIGHLIGHT_REQUEST_OBJECT;
    const validation = HIGHLIGHT_REQUEST_VALIDATION;
    const isValid = IS_HIGHLIGHT_REQUEST_VALID;

    const [row, setRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(object))));

    // Initialize the temporary row object with existing values.
    React.useEffect(() => {
        const newRow = {
            ...row,
            ...existingRow,

            _max_length_secs: existingRow.constraints?.max_length_secs ?? "",
            _min_timestamp_secs: existingRow.constraints?.min_timestamp_secs ?? "",

            _recurring_process: existingRow.constraints?.recurring_process ?? true,

            _diversity_weight: existingRow.constraints?.diversity_weight ?? "",
            _threshold: existingRow.constraints?.threshold ?? "",

            _show_accordion: false,

            // Convert to dayjs format if datetime exists.
            created_on: existingRow.created_on ? dayjs(existingRow.created_on) : null,
            updated_on: existingRow.updated_on ? dayjs(existingRow.updated_on) : null,

            form: {
                ...row.form,
                ...existingRow.form,
                saved: false // Required to allow editing input fields.
            }
        };

        setInsertObjectState(setRow, newRow);
    }, []);

    /**
     * Executed when typing something into an input field.
     */
    const onChange = (obj, key, value) => {
        const newRow = JSON.parse(JSON.stringify(row));
        setValue(newRow, key, value);

        if (key === "_max_length_secs") {
            newRow.constraints.max_length_secs = isNaN(value) || !value ? null : parseInt(value);
        }

        if (key === "_min_timestamp_secs") {
            newRow.constraints.min_timestamp_secs = isNaN(value) || !value ? null : parseInt(value);
        }

        if (key === "_recurring_process") {
            newRow.constraints.recurring_process = value === "true" || value === true;
        }

        if (key === "_diversity_weight") {
            newRow.constraints.diversity_weight = isNaN(value) || !value ? null : parseFloat(value);
        }

        if (key === "_threshold") {
            newRow.constraints.threshold = isNaN(value) || !value ? null : parseFloat(value);
        }

        if (key === "_show_accordion") {
            if (value) {
                newRow.constraints.library_ids = [];
                newRow.constraints.resource_ids = [];
                newRow.constraints.author_ids = [];
            } else {
                newRow.constraints.library_ids = existingRow?.constraints?.library_ids ?? [];
                newRow.constraints.resource_ids = existingRow?.constraints?.resource_ids ?? [];
                newRow.constraints.author_ids = existingRow?.constraints?.author_ids ?? [];
            }
        }

        validateRow(obj, validation, newRow);
        setInsertObjectState(setRow, newRow);
    };

    const handleClick = () => {
        if (!isValid(row)) {
            props.showSnackbar({ message: getLanguage().SNACKBAR_ERROR_FORM_INVALID, color: "error" });
            return;
        }

        const options = { row };
        callback(true, options);
    };

    const handleLibrary = (selected = []) => {
        const ids = selected.map((el) => el.id);
        const constraints = { ...row.constraints, library_ids: ids };
        onChange(row, "constraints", constraints);
    };

    const handleResources = (selected = []) => {
        const ids = selected.map((el) => el.id);
        const constraints = { ...row.constraints, resource_ids: ids };
        onChange(row, "constraints", constraints);
    };

    const handleAuthors = (selected = []) => {
        const ids = selected.map((el) => el.id);
        const constraints = { ...row.constraints, author_ids: ids };
        onChange(row, "constraints", constraints);
    };

    // TODO: does not accept only IDs, wants full data: selected={existingRow?.constraints?.author_ids ?? null}
    const accordionValues = [
        {
            title: "Library",
            description: "Select a library to search for highlights.",
            component: (
                <CustomSelection
                    {...props}
                    callback={handleLibrary}
                    query={querySelectLibraries}
                    getPrimarySearchText={(el) => el?.name}
                    getSecondarySearchText={(el) => el?.description}
                    getPrimaryText={(el) => getEllipsis(el?.name, 50)}
                    getSecondaryText={(el) => getEllipsis(el?.description, 50)}
                />
            )
        },
        {
            title: "Resources",
            description: "Select one or more resources to search for highlights.",
            component: (
                <CustomSelection
                    {...props}
                    multiselect
                    callback={handleResources}
                    query={querySelectResources}
                    getPrimarySearchText={(el) => el?.name}
                    getSecondarySearchText={(el) => el?.description}
                    getPrimaryText={(el) => getEllipsis(el?.name, 50)}
                    getSecondaryText={(el) => getEllipsis(el?.description, 50)}
                />
            )
        },
        {
            title: "Authors",
            description: "Select one or more authors to search for highlights.",
            component: (
                <CustomSelection
                    {...props}
                    multiselect
                    callback={handleAuthors}
                    query={querySelectAuthors}
                    getPrimarySearchText={(el) => el?.first_name}
                    getSecondarySearchText={(el) => el?.last_name}
                    getPrimaryText={(el) => `${el?.first_name} ${el?.last_name}`}
                    getSecondaryText={(el) => getEllipsis(el?.bio, 50)}
                />
            )
        }
    ];

    return (
        <Box className="box">
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <CustomInput name="name" row={row} onChange={onChange} label="Name" />
                </Grid>

                <Grid item xs={12}>
                    <CustomInput name="description" row={row} onChange={onChange} label="Description" multiline minRows={2} maxRows={5} />
                </Grid>

                <Grid item xs={6}>
                    <CustomTooltip
                        form={<CustomInput name="_max_length_secs" row={row} onChange={onChange} label="Maximum length of clip (s)" type="number" />}
                        tooltip="The approximate maximum length of the highlight clip in seconds. The actual length may slightly vary based on the content."
                    />
                </Grid>

                <Grid item xs={6}>
                    <CustomTooltip
                        form={<CustomInput name="_min_timestamp_secs" row={row} onChange={onChange} label="Minimum timestamp (s)" type="number" />}
                        tooltip="The minimum timestamp in seconds from which the highlight clip can start. This helps to filter out highlights that are too early in the resource."
                    />
                </Grid>

                <Grid item xs={6}>
                    <CustomTooltip
                        form={<CustomInput name="_diversity_weight" row={row} onChange={onChange} label="Resource diversity weight" type="number" step={0.1} />}
                        tooltip="The resource diversity weight determines how much the search algorithm prioritizes using less-utilized resources. A higher value means more varied resources will be selected, while a lower value will favor strict transcript similarity to the query."
                    />
                </Grid>

                <Grid item xs={6}>
                    <CustomTooltip
                        form={<CustomInput name="_threshold" row={row} onChange={onChange} label="Similarity threshold" step={0.1} />}
                        tooltip="Determines how similar the highlights must be to the query. A lower value means more results will be returned, while a higher value will filter out less relevant highlights."
                    />
                </Grid>

                <Grid item xs={12}>
                    <Stack direction="row" justifyContent="center" alignItems="center">
                        <CustomInput name="_recurring_process" row={row} onChange={onChange} checkbox />
                        <Typography variant="body2" color="textSecondary">
                            {"Process same request multiple times"}
                        </Typography>
                    </Stack>
                </Grid>

                <Grid item xs={12} style={{ textAlign: "center" }}>
                    <CustomInput name="_show_accordion" row={row} onChange={onChange} label="Recreate library, resource, and author filters" switch />
                </Grid>

                {/* Show accordion only if the switch is enabled. */}
                {row._show_accordion && (
                    <Grid item xs={12}>
                        <ControlledAccordion {...props} startClosed values={accordionValues} />
                    </Grid>
                )}
            </Grid>

            <CustomButton {...props} title={"Update highlight request"} onClick={handleClick} className="button" fullWidth />
        </Box>
    );
}
